/**
 * Notifications — four sub-tabs (All / My / Logs / Channels). Phase 3
 * lands the real subsystem: rules table + worker scheduler + email
 * channel + delivery logs. Channels tab Email tile is real now;
 * Telegram tile arrives in Phase 4.
 */

import { useEffect, useState } from "react";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { StatusToast } from "@/components/StatusToast";
import {
  useCreateNotification,
  useDeleteNotification,
  useDeleteTelegramChat,
  useNotificationDeliveries,
  useNotifications,
  useTelegramConnect,
  useTelegramDisconnect,
  useTelegramRefreshChats,
  useTelegramStatus,
  useTelegramTestSend,
  useUpdateNotification,
  type NotifSchedule,
  type NotificationRow,
} from "@/lib/api-hooks";
import { useAuth } from "@/lib/useAuth";
import { CustomScheduleModal } from "./CustomScheduleModal";
import { StConfirmModal, StSectionHead, StStatusDot } from "./_shared";

type SubId = "all" | "my" | "logs" | "channels";

const SUB_TABS: { id: SubId; label: string }[] = [
  { id: "all", label: "All Notifications" },
  { id: "my", label: "My Notifications" },
  { id: "logs", label: "Logs" },
  { id: "channels", label: "Channels" },
];

const NOTIF_TYPES: { value: NotificationRow["type"]; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "changes", label: "Changes" },
  { value: "main", label: "Main Metrics Change" },
  { value: "goal", label: "Goal Achieved" },
  { value: "ua", label: "UA Report" },
  { value: "weekly", label: "Weekly Report" },
  { value: "weekly_extended", label: "Weekly Report Extended" },
  { value: "autopilot_digest", label: "Autopilot digest" },
];

const SCHEDULE_KINDS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom…" },
] as const;

export function NotificationsTab() {
  const [sub, setSub] = useState<SubId>("all");
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 24,
        alignItems: "start",
      }}
    >
      <nav className="card" style={{ padding: 8 }}>
        {SUB_TABS.map((s) => (
          <div
            key={s.id}
            className={`nav-item ${sub === s.id ? "active" : ""}`}
            onClick={() => setSub(s.id)}
            style={{ cursor: "pointer" }}
          >
            <span>{s.label}</span>
          </div>
        ))}
      </nav>
      <div>
        {sub === "all" && <NotifAll />}
        {sub === "my" && <NotifMine />}
        {sub === "logs" && <NotifLogs />}
        {sub === "channels" && <NotifChannels />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// All Notifications
// ---------------------------------------------------------------------------

function fmtSchedule(s: NotifSchedule | null | undefined): string {
  if (!s) return "—";
  if (s.kind === "daily") return "Daily";
  if (s.kind === "weekly") return `Weekly · ${s.weekday}`;
  if (s.kind === "monthly") return `Monthly · day ${s.day_of_month}`;
  if (s.kind === "custom" && s.mode === "ordinal") {
    return `Custom · ${s.ordinal_label}`;
  }
  if (s.kind === "custom" && s.mode === "dates") {
    return `Custom · ${s.dates.length} dates`;
  }
  return "—";
}

/** CSV download for a single delivery row (Logs tab "Report"
 *  button). Generated entirely client-side — no extra endpoint. */
function downloadDeliveryCsv(d: import("@/lib/api-hooks").NotificationDelivery) {
  const esc = (v: string | number | null) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headers = [
    "delivery_id",
    "notification_id",
    "notification_name",
    "notification_type",
    "started_at",
    "finished_at",
    "channel",
    "recipient",
    "status",
    "error_message",
    "payload_summary",
  ];
  const row = [
    d.id,
    d.notification_id,
    d.notification_name,
    d.notification_type,
    d.started_at,
    d.finished_at,
    d.channel,
    d.recipient,
    d.status,
    d.error_message,
    d.payload_summary,
  ].map(esc);
  const csv = `${headers.join(",")}\n${row.join(",")}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `notification-delivery-${d.id}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

type DraftValues = {
  name: string;
  type: NotificationRow["type"] | "";
  channel: string;
  scheduleKind: "daily" | "weekly" | "monthly" | "custom" | "";
  weekday: string;
  dayOfMonth: number;
  customSchedule: NotifSchedule | null;
  time: string;
};

const EMPTY_DRAFT: DraftValues = {
  name: "",
  type: "",
  channel: "email",
  scheduleKind: "",
  weekday: "Monday",
  dayOfMonth: 1,
  customSchedule: null,
  time: "09:00",
};

function valuesFromNotif(n: NotificationRow): DraftValues {
  const s = n.schedule;
  let scheduleKind: DraftValues["scheduleKind"] = "";
  let weekday = "Monday";
  let dayOfMonth = 1;
  let customSchedule: NotifSchedule | null = null;
  if (s.kind === "daily") scheduleKind = "daily";
  else if (s.kind === "weekly") {
    scheduleKind = "weekly";
    weekday = s.weekday;
  } else if (s.kind === "monthly") {
    scheduleKind = "monthly";
    dayOfMonth = s.day_of_month;
  } else if (s.kind === "custom") {
    scheduleKind = "custom";
    customSchedule = s;
  }
  return {
    name: n.name,
    type: n.type,
    channel: n.channel,
    scheduleKind,
    weekday,
    dayOfMonth,
    customSchedule,
    time: n.time_local,
  };
}

function buildScheduleFromValues(v: DraftValues): NotifSchedule {
  if (v.scheduleKind === "weekly") return { kind: "weekly", weekday: v.weekday };
  if (v.scheduleKind === "monthly") return { kind: "monthly", day_of_month: v.dayOfMonth };
  if (v.scheduleKind === "custom" && v.customSchedule) return v.customSchedule;
  return { kind: "daily" };
}

function isComplete(v: DraftValues): boolean {
  return (
    !!v.name &&
    !!v.type &&
    !!v.channel &&
    !!v.scheduleKind &&
    (v.scheduleKind !== "custom" || v.customSchedule != null) &&
    !!v.time
  );
}

function NotifAll() {
  const list = useNotifications(false);
  const create = useCreateNotification();
  const update = useUpdateNotification();
  const del = useDeleteNotification();
  const tg = useTelegramStatus();
  const channelOptions: { value: string; label: string }[] = [
    { value: "email", label: "Email" },
    ...(tg.data?.connected
      ? tg.data.chats
          .filter((c) => c.active)
          .map((c) => ({
            value: `telegram:${c.chat_id}`,
            label: `Telegram · ${c.title}`,
          }))
      : []),
  ];

  const rows = list.data ?? [];
  // Per-row editing state. `editingId === "draft"` for the always-
  // visible blank row at the bottom; numeric id for an existing
  // notification being edited; null = nothing in edit mode.
  const [editingId, setEditingId] = useState<number | "draft" | null>("draft");
  const [draftValues, setDraftValues] = useState<DraftValues>(EMPTY_DRAFT);
  // Edit-mode buffer for existing rows. Keyed by notification id.
  const [editBuffers, setEditBuffers] = useState<Record<number, DraftValues>>({});
  const [confirmDel, setConfirmDel] = useState<NotificationRow | null>(null);
  // Which row's Custom-schedule modal is open. "draft" or numeric id.
  const [customFor, setCustomFor] = useState<number | "draft" | null>(null);

  const startEdit = (n: NotificationRow) => {
    setEditBuffers((prev) => ({ ...prev, [n.id]: valuesFromNotif(n) }));
    setEditingId(n.id);
  };

  const cancelEdit = () => {
    if (typeof editingId === "number") {
      setEditBuffers((prev) => {
        const { [editingId]: _, ...rest } = prev;
        return rest;
      });
    }
    setEditingId("draft");
  };

  const updateBuffer = (id: number | "draft", patch: Partial<DraftValues>) => {
    if (id === "draft") {
      setDraftValues((d) => ({ ...d, ...patch }));
    } else {
      setEditBuffers((prev) => ({
        ...prev,
        [id]: { ...prev[id], ...patch },
      }));
    }
  };

  const saveDraft = async () => {
    if (
      !isComplete(draftValues) ||
      draftValues.type === "" ||
      draftValues.scheduleKind === ""
    )
      return;
    await create.mutateAsync({
      name: draftValues.name,
      type: draftValues.type,
      channel: draftValues.channel,
      schedule: buildScheduleFromValues(draftValues),
      time_local: draftValues.time,
    });
    setDraftValues(EMPTY_DRAFT);
  };

  const saveExisting = async (id: number) => {
    const buf = editBuffers[id];
    if (!buf || !isComplete(buf) || buf.type === "" || buf.scheduleKind === "")
      return;
    await update.mutateAsync({
      id,
      patch: {
        name: buf.name,
        type: buf.type,
        channel: buf.channel,
        schedule: buildScheduleFromValues(buf),
        time_local: buf.time,
      },
    });
    setEditBuffers((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    setEditingId("draft");
  };

  const copyRow = async (n: NotificationRow) => {
    await create.mutateAsync({
      name: `${n.name} (copy)`,
      type: n.type,
      channel: n.channel,
      schedule: n.schedule,
      time_local: n.time_local,
      enabled: false,
    });
  };

  const openCustom = (id: number | "draft") => setCustomFor(id);
  const customScheduleValue: NotifSchedule | null =
    customFor === "draft"
      ? draftValues.customSchedule
      : customFor != null
        ? editBuffers[customFor]?.customSchedule ?? null
        : null;
  const onCustomApply = (sched: NotifSchedule) => {
    if (customFor === null) return;
    updateBuffer(customFor, { customSchedule: sched });
    setCustomFor(null);
  };
  const onCustomCancel = () => {
    if (customFor === null) return;
    const cur =
      customFor === "draft"
        ? draftValues
        : editBuffers[customFor];
    if (!cur?.customSchedule) {
      updateBuffer(customFor, { scheduleKind: "" });
    }
    setCustomFor(null);
  };

  return (
    <>
      <StSectionHead
        title="All Notifications"
        desc="Create and manage every notification rule across the organization."
      />

      {rows.map((n, idx) => {
        const isEditing = editingId === n.id;
        const values = isEditing ? editBuffers[n.id] : valuesFromNotif(n);
        return (
          <RowEditor
            key={n.id}
            number={idx + 1}
            isDraft={false}
            isEditing={isEditing}
            values={values ?? valuesFromNotif(n)}
            channelOptions={channelOptions}
            enabled={n.enabled}
            onChange={(patch) => updateBuffer(n.id, patch)}
            onOpenCustom={() => openCustom(n.id)}
            onEdit={() => startEdit(n)}
            onCancel={cancelEdit}
            onSave={() => void saveExisting(n.id)}
            onCopy={() => void copyRow(n)}
            onDelete={() => setConfirmDel(n)}
            onToggleActive={() =>
              update.mutate({ id: n.id, patch: { enabled: !n.enabled } })
            }
            saving={update.isPending}
          />
        );
      })}

      {/* Always-visible draft row at the bottom for adding new */}
      <RowEditor
        number={rows.length + 1}
        isDraft
        isEditing
        values={draftValues}
        channelOptions={channelOptions}
        enabled={false}
        onChange={(patch) => updateBuffer("draft", patch)}
        onOpenCustom={() => openCustom("draft")}
        onSave={() => void saveDraft()}
        saving={create.isPending}
      />

      {confirmDel && (
        <StConfirmModal
          open={!!confirmDel}
          title="Delete notification?"
          body="This will stop all future deliveries. The history stays in Logs."
          danger
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            void del.mutate(confirmDel.id);
            setConfirmDel(null);
          }}
        />
      )}

      <CustomScheduleModal
        open={customFor !== null}
        value={customScheduleValue}
        onCancel={onCustomCancel}
        onApply={onCustomApply}
      />
    </>
  );
}

function RowEditor({
  number,
  isDraft,
  isEditing,
  values,
  channelOptions,
  enabled,
  onChange,
  onOpenCustom,
  onEdit,
  onCancel,
  onSave,
  onCopy,
  onDelete,
  onToggleActive,
  saving,
}: {
  number: number;
  isDraft: boolean;
  isEditing: boolean;
  values: DraftValues;
  channelOptions: { value: string; label: string }[];
  enabled: boolean;
  onChange: (patch: Partial<DraftValues>) => void;
  onOpenCustom: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave: () => void;
  onCopy?: () => void;
  onDelete?: () => void;
  onToggleActive?: () => void;
  saving: boolean;
}) {
  const editable = isEditing;
  const showGreenTint = !isDraft && enabled && !isEditing;
  const complete = isComplete(values);
  return (
    <div
      className="card"
      style={{
        marginBottom: 10,
        background: showGreenTint
          ? "linear-gradient(180deg,#ecfaf0,#fff)"
          : isDraft
            ? "var(--c-surface-sunk)"
            : "var(--c-surface)",
        border: showGreenTint
          ? "1px solid #b9e6c5"
          : isDraft
            ? "1px dashed var(--c-border)"
            : "1px solid var(--c-divider)",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          display: "grid",
          gridTemplateColumns:
            "36px minmax(180px,1.4fr) minmax(170px,1fr) minmax(200px,1.2fr) minmax(150px,1fr) 110px auto 50px",
          gap: 10,
          alignItems: "end",
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "var(--c-text-3)",
            fontWeight: 600,
            paddingBottom: 9,
          }}
        >
          {isDraft ? "—" : `#${number}`}
        </div>

        <div className="field" style={{ minWidth: 0 }}>
          <label className="label">Name</label>
          <input
            className="input"
            disabled={!editable}
            placeholder="e.g. Morning overview"
            value={values.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </div>

        <div className="field" style={{ minWidth: 0 }}>
          <label className="label">Type</label>
          <select
            className="select"
            disabled={!editable}
            value={values.type}
            onChange={(e) =>
              onChange({
                type: e.target.value as NotificationRow["type"] | "",
              })
            }
          >
            <option value="">Select…</option>
            {NOTIF_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ minWidth: 0 }}>
          <label className="label">Channel</label>
          <select
            className="select"
            disabled={!editable}
            value={values.channel}
            onChange={(e) => onChange({ channel: e.target.value })}
          >
            {channelOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ minWidth: 0 }}>
          <label className="label">Schedule</label>
          <select
            className="select"
            disabled={!editable}
            value={values.scheduleKind}
            onChange={(e) => {
              const v = e.target.value as DraftValues["scheduleKind"];
              onChange({
                scheduleKind: v,
                customSchedule: v === "custom" ? values.customSchedule : null,
              });
              if (v === "custom") onOpenCustom();
            }}
          >
            <option value="">Select…</option>
            {SCHEDULE_KINDS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {values.scheduleKind === "custom" && values.customSchedule && (
            <div
              style={{
                fontSize: 11,
                color: "var(--c-text-3)",
                marginTop: 4,
              }}
            >
              {fmtSchedule(values.customSchedule)}{" "}
              {editable && (
                <button
                  type="button"
                  onClick={onOpenCustom}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--c-accent-600)",
                    cursor: "pointer",
                    fontWeight: 500,
                    padding: 0,
                    marginLeft: 4,
                    fontSize: 11,
                  }}
                >
                  edit
                </button>
              )}
            </div>
          )}
        </div>

        <div className="field" style={{ minWidth: 0 }}>
          <label className="label">Time</label>
          <input
            className="input"
            type="time"
            disabled={!editable}
            value={values.time}
            onChange={(e) => onChange({ time: e.target.value })}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            paddingBottom: 4,
          }}
        >
          {isEditing ? (
            <>
              {!isDraft && onCancel && (
                <Btn onClick={onCancel} disabled={saving}>
                  Cancel
                </Btn>
              )}
              <Btn
                variant="primary"
                disabled={!complete || saving}
                onClick={onSave}
              >
                Save
              </Btn>
            </>
          ) : (
            <>
              {onEdit && (
                <button
                  type="button"
                  className="btn icon sm"
                  title="Edit"
                  onClick={onEdit}
                >
                  {I.edit}
                </button>
              )}
              {onCopy && (
                <button
                  type="button"
                  className="btn icon sm"
                  title="Copy"
                  onClick={onCopy}
                >
                  {I.copy}
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="btn icon sm danger"
                  title="Delete"
                  onClick={onDelete}
                >
                  {I.trash}
                </button>
              )}
            </>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingBottom: 6,
          }}
        >
          {!isDraft && onToggleActive && (
            <ToggleSwitch on={enabled} onChange={onToggleActive} />
          )}
        </div>
      </div>

      {/* Inline weekday / day-of-month picker when those schedule
       *  kinds are selected — keeps the row compact while exposing
       *  the secondary picker just below the main row. */}
      {editable && values.scheduleKind === "weekly" && (
        <div
          style={{
            padding: "0 16px 12px 52px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span className="muted" style={{ fontSize: 12 }}>
            Weekday:
          </span>
          <select
            className="select"
            value={values.weekday}
            onChange={(e) => onChange({ weekday: e.target.value })}
          >
            {[
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ].map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </div>
      )}
      {editable && values.scheduleKind === "monthly" && (
        <div
          style={{
            padding: "0 16px 12px 52px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span className="muted" style={{ fontSize: 12 }}>
            Day of month:
          </span>
          <input
            className="input"
            type="number"
            min={1}
            max={28}
            value={values.dayOfMonth}
            onChange={(e) =>
              onChange({
                dayOfMonth: Math.max(
                  1,
                  Math.min(28, Number(e.target.value) || 1),
                ),
              })
            }
            style={{ width: 80 }}
          />
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  on,
  onChange,
}: {
  on: boolean;
  onChange: () => void;
}) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        cursor: "pointer",
        position: "relative",
      }}
      onClick={(e) => {
        e.preventDefault();
        onChange();
      }}
    >
      <span
        style={{
          width: 34,
          height: 20,
          borderRadius: 999,
          background: on ? "var(--c-pos-500)" : "var(--c-border-strong)",
          position: "relative",
          transition: "background .15s",
          display: "inline-block",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: on ? 16 : 2,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#fff",
            transition: "left .15s",
          }}
        />
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// My Notifications
// ---------------------------------------------------------------------------

function NotifMine() {
  const list = useNotifications(true);
  const update = useUpdateNotification();
  const rows = list.data ?? [];

  return (
    <>
      <StSectionHead
        title="My Notifications"
        desc="Notifications addressed to you. Toggle anything off to stop receiving it; the rule itself stays active for other recipients."
      />
      <div className="card">
        <table className="dt">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Channel</th>
              <th>Schedule</th>
              <th style={{ width: 80, textAlign: "right" }}>Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="muted"
                  style={{ padding: 30, textAlign: "center" }}
                >
                  You have no personal notifications yet. Create one in the All
                  tab.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const typeLabel =
                NOTIF_TYPES.find((t) => t.value === r.type)?.label ?? r.type;
              return (
                <tr key={r.id}>
                  <td>
                    <b>{r.name}</b>
                  </td>
                  <td className="muted">{typeLabel}</td>
                  <td className="muted">{r.channel}</td>
                  <td className="muted">
                    {fmtSchedule(r.schedule)} · {r.time_local}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <ToggleSwitch
                      on={r.enabled}
                      onChange={() =>
                        update.mutate({
                          id: r.id,
                          patch: { enabled: !r.enabled },
                        })
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Logs
// ---------------------------------------------------------------------------

function NotifLogs() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const offset = (page - 1) * pageSize;
  const query = useNotificationDeliveries({ limit: pageSize, offset });
  const data = query.data;
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <StSectionHead
        title="Notification Logs"
        desc="Every delivery attempt with channel, recipient and status."
      />
      <div className="card">
        <table className="dt">
          <thead>
            <tr>
              <th>Date / Time</th>
              <th>Notification</th>
              <th>Type</th>
              <th>Channel</th>
              <th>Recipient</th>
              <th>Status</th>
              <th style={{ width: 110, textAlign: "right" }}>Report</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr>
                <td colSpan={7} className="muted" style={{ padding: 20 }}>
                  Loading…
                </td>
              </tr>
            )}
            {(data?.rows ?? []).map((r) => {
              const typeLabel =
                NOTIF_TYPES.find((t) => t.value === r.notification_type)?.label ??
                r.notification_type;
              return (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {new Date(r.started_at).toLocaleString()}
                  </td>
                  <td>
                    <b>{r.notification_name ?? "—"}</b>
                  </td>
                  <td className="muted">{typeLabel}</td>
                  <td className="muted">{r.channel}</td>
                  <td className="muted">{r.recipient}</td>
                  <td>
                    <DeliveryStatusPill status={r.status} error={r.error_message} />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Btn
                      icon={I.download}
                      onClick={() => downloadDeliveryCsv(r)}
                    >
                      Report
                    </Btn>
                  </td>
                </tr>
              );
            })}
            {!query.isLoading && (data?.rows ?? []).length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="muted"
                  style={{ padding: 30, textAlign: "center" }}
                >
                  No deliveries yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderTop: "1px solid var(--c-divider)",
            fontSize: 12.5,
            color: "var(--c-text-3)",
          }}
        >
          <div>
            {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, total)} of {total}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Btn disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </Btn>
            <span style={{ alignSelf: "center" }}>
              Page {page} of {pages}
            </span>
            <Btn
              disabled={page === pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              Next
            </Btn>
          </div>
        </div>
      </div>
    </>
  );
}

function DeliveryStatusPill({
  status,
  error,
}: {
  status: "delivered" | "failed" | "pending";
  error: string | null;
}) {
  const map = {
    delivered: { bg: "var(--c-pos-50)", fg: "var(--c-pos-600)", label: "Delivered" },
    failed: { bg: "var(--c-neg-50)", fg: "var(--c-neg-600)", label: "Failed" },
    pending: { bg: "var(--c-surface-sunk)", fg: "var(--c-text-3)", label: "Pending" },
  };
  const c = map[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        background: c.bg,
        color: c.fg,
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 500,
      }}
      title={error ?? undefined}
    >
      {c.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Channels — Email tile real, Telegram tile placeholder for Phase 4
// ---------------------------------------------------------------------------

function NotifChannels() {
  const { user } = useAuth();
  return (
    <>
      <StSectionHead
        title="Channels"
        desc="Where notifications go. Email is per-user; the Telegram bot can deliver to many private chats, groups and channels."
      />
      <div
        style={{
          display: "grid",
          // `minmax(0, 1fr)` (вместо просто `1fr`) разрешает колонке
          // ужаться меньше min-content child'а. Без этого если внутри
          // Telegram-тайла окажется любой элемент с широкой min-width
          // (например `.dt` с глобальным min-width: 1600px), grid-cell
          // расширяется и выезжает за viewport вправо.
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 18,
          alignItems: "start",
        }}
      >
        {/* Email tile */}
        <div className="card card-pad" style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--c-surface-sunk)",
                display: "grid",
                placeItems: "center",
                color: "var(--c-text-2)",
              }}
            >
              {I.bell}
            </div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Email</h3>
          </div>
          <div
            className="muted"
            style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}
          >
            Notifications addressed to you go to your login email. To change
            your login email, see Profile → Email.
          </div>
          <div
            className="card"
            style={{
              padding: "12px 14px",
              marginBottom: 14,
              background: "var(--c-surface-sunk)",
            }}
          >
            <div style={{ fontSize: 11, color: "var(--c-text-3)", marginBottom: 4 }}>
              Email
            </div>
            <div className="mono" style={{ fontSize: 13 }}>
              {user?.email ?? "—"}
            </div>
          </div>
        </div>

        {/* Telegram tile — real Phase 4 implementation */}
        <div style={{ minWidth: 0 }}>
          <TelegramTile />
        </div>
      </div>
    </>
  );
}

function TelegramTile() {
  const status = useTelegramStatus();
  const connect = useTelegramConnect();
  const disconnect = useTelegramDisconnect();
  const refresh = useTelegramRefreshChats();
  const testSend = useTelegramTestSend();
  const delChat = useDeleteTelegramChat();

  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [showInstr, setShowInstr] = useState(false);
  const [confirmDisc, setConfirmDisc] = useState(false);
  const [confirmDelChat, setConfirmDelChat] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Floating toast that fires after a Test / Send-test-to-all click.
  // Auto-dismiss is the caller's responsibility — useEffect below
  // clears it 3.5s after each set.
  const [testToast, setTestToast] = useState<
    { msg: string; variant: "success" | "error" } | null
  >(null);
  useEffect(() => {
    if (!testToast) return;
    const t = window.setTimeout(() => setTestToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [testToast]);

  /**
   * Wrap a Test / Send-test-to-all click. Reads the {delivered, error}
   * pair the API returns and pushes a green or red toast accordingly.
   * Caller passes the chat title so the toast can name the recipient.
   */
  const fireTestSend = async (
    chatId: number,
    chatTitle: string,
  ): Promise<boolean> => {
    try {
      const res = await testSend.mutateAsync({ chat_id: chatId });
      if (res.delivered) {
        setTestToast({
          msg: `Тестовое сообщение отправлено в ${chatTitle}`,
          variant: "success",
        });
        return true;
      }
      setTestToast({
        msg: `Не доставлено в ${chatTitle}: ${res.error ?? "unknown error"}`,
        variant: "error",
      });
      return false;
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Server error";
      setTestToast({
        msg: `Ошибка отправки в ${chatTitle}: ${msg}`,
        variant: "error",
      });
      return false;
    }
  };

  const data = status.data;
  const connected = data?.connected ?? false;
  // Platform bot (@oneadspro_bot): no token of the org's own, nothing to
  // disconnect, and chats arrive through personal links (Profile →
  // Notification channels → Connect Telegram), not through "Refresh chats".
  const isPlatform = data?.is_platform ?? false;

  const onValidate = async () => {
    setErrorMsg(null);
    try {
      await connect.mutateAsync(token.trim());
      setToken("");
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response
          ?.data?.error?.message ?? "Failed to validate token";
      setErrorMsg(msg);
    }
  };

  return (
    <div className="card card-pad">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: "#2aabee",
            display: "grid",
            placeItems: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          TG
        </div>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          Telegram Bot
        </h3>
        <div style={{ flex: 1 }} />
        <StStatusDot kind={connected ? "ok" : "off"} />
      </div>
      <div
        className="muted"
        style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}
      >
        Connect a Telegram bot to receive notifications in private chats,
        groups, or channels. One bot can deliver to many recipients with
        different schedules.
      </div>

      {/* Block A — Bot setup (token + username always visible) */}
      {isPlatform && (
        <div
          className="muted"
          style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}
        >
          Your organisation uses the platform bot{" "}
          <b>@{data?.username}</b> — no token needed. To add a recipient,
          open Profile → Notification channels → Connect Telegram and press
          Start in the chat (private or group) where you want reports.
          Connecting a bot of your own below replaces it for this
          organisation.
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div className="field">
          <label className="label">Bot token</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="input mono"
              type={showToken ? "text" : "password"}
              value={connected && !isPlatform ? "••••••••••••••••••••••" : token}
              onChange={(e) => (!connected || isPlatform) && setToken(e.target.value)}
              placeholder="••••:AAH…"
              readOnly={connected && !isPlatform}
            />
            <button
              type="button"
              className="btn icon sm"
              title={showToken ? "Hide" : "Show"}
              onClick={() => setShowToken((v) => !v)}
              disabled={connected && !isPlatform}
            >
              {showToken ? I.x : I.eye}
            </button>
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--c-text-3)",
              marginTop: 4,
            }}
          >
            Get your token from @BotFather → /newbot
          </div>
        </div>
        <div className="field">
          <label className="label">Bot username</label>
          <input
            className="input mono"
            readOnly
            value={connected && data?.username ? `@${data.username}` : "—"}
            placeholder="@your_bot"
          />
        </div>
      </div>
      {errorMsg && (!connected || isPlatform) && (
        <div
          style={{
            color: "var(--c-neg-600)",
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          {errorMsg}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {connected && !isPlatform ? (
          <Btn variant="danger" onClick={() => setConfirmDisc(true)}>
            Disconnect
          </Btn>
        ) : (
          <Btn
            variant="primary"
            icon={I.check}
            disabled={!token.trim() || connect.isPending}
            onClick={() => void onValidate()}
          >
            Validate &amp; Connect
          </Btn>
        )}
      </div>

      {/* Block B — Recipients (only when connected) */}
      {connected && data && (
        <>
          {data.bot_id != null && (
            <div
              className="muted mono"
              style={{ fontSize: 11, marginBottom: 8 }}
            >
              Bot ID: {data.bot_id}
              {data.last_validated_at &&
                ` · validated ${new Date(data.last_validated_at).toLocaleString()}`}
            </div>
          )}
          {/* Recipients */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>Recipients</div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn
                icon={I.refresh}
                onClick={() => void refresh.mutate()}
                disabled={refresh.isPending}
              >
                Refresh chats
              </Btn>
              <Btn icon={I.plus} onClick={() => setShowInstr((s) => !s)}>
                Add chat
              </Btn>
            </div>
          </div>
          {showInstr && (
            <div
              className="card"
              style={{
                padding: "12px 14px",
                marginBottom: 10,
                background: "var(--c-accent-50)",
                border: "1px solid var(--c-accent-100)",
                fontSize: 12.5,
                color: "var(--c-text-2)",
                lineHeight: 1.55,
              }}
            >
              {isPlatform ? (
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    Profile → Notification channels → <b>Connect Telegram</b>
                  </li>
                  <li>
                    Private chat: press Start. Group: add @{data?.username}{" "}
                    to the group and paste the start command there
                  </li>
                  <li>The chat appears here within seconds</li>
                </ol>
              ) : (
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Add the bot to your chat/channel as admin</li>
                  <li>
                    Send <code>/start</code> in that chat
                  </li>
                  <li>
                    Click <b>Refresh chats</b> — new chats appear here
                  </li>
                </ol>
              )}
            </div>
          )}
          {/* `.dt` carries a global min-width: 1600px (Ads-Manager
           *  grids). Inside the Channels card it would shove the
           *  whole page horizontally — same bug as the Profile orgs
           *  table. Override locally so the recipients table fits
           *  the card's width. */}
          <div className="card" style={{ marginBottom: 14 }}>
            <table className="dt" style={{ minWidth: 0 }}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Chat ID</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th style={{ width: 160, textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.chats.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="muted"
                      style={{ padding: 20, textAlign: "center" }}
                    >
                      No recipients yet. Add the bot to a chat then click
                      Refresh.
                    </td>
                  </tr>
                )}
                {data.chats.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className="badge">{c.type}</span>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {c.chat_id}
                    </td>
                    <td>
                      <b>{c.title}</b>
                    </td>
                    <td>
                      <span className={c.active ? "badge enabled" : "badge"}>
                        <span className="dot" />
                        {c.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <Btn
                          onClick={() =>
                            void fireTestSend(c.chat_id, c.title || c.type)
                          }
                          disabled={testSend.isPending}
                        >
                          Test
                        </Btn>
                        <Btn
                          variant="danger"
                          onClick={() =>
                            setConfirmDelChat({ id: c.id, title: c.title })
                          }
                        >
                          Delete
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Block C — Connection info + Send test to all */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              border: "1px solid var(--c-divider)",
              borderRadius: 10,
              background: "var(--c-surface-sunk)",
            }}
          >
            <div style={{ fontSize: 12.5, color: "var(--c-text-2)" }}>
              {data.last_message_at ? (
                <>
                  Last message sent:{" "}
                  <b>
                    {new Date(data.last_message_at).toLocaleString()}
                  </b>
                </>
              ) : (
                "No messages sent yet."
              )}
            </div>
            <Btn
              icon={I.bell}
              disabled={data.chats.length === 0 || testSend.isPending}
              onClick={async () => {
                const active = data.chats.filter((x) => x.active);
                let ok = 0;
                let fail = 0;
                for (const c of active) {
                  // Don't fire individual toasts during a fan-out;
                  // collect counts and show one summary toast.
                  try {
                    const res = await testSend.mutateAsync({
                      chat_id: c.chat_id,
                    });
                    if (res.delivered) ok++;
                    else fail++;
                  } catch {
                    fail++;
                  }
                }
                setTestToast(
                  fail === 0
                    ? {
                        msg: `Тестовое сообщение отправлено в ${ok} ${
                          ok === 1 ? "чат" : "чатов"
                        }`,
                        variant: "success",
                      }
                    : {
                        msg: `Доставлено: ${ok}, не доставлено: ${fail}`,
                        variant: "error",
                      },
                );
              }}
            >
              Send test to all
            </Btn>
          </div>
        </>
      )}

      <StConfirmModal
        open={confirmDisc}
        title="Disconnect Telegram bot?"
        body="All scheduled deliveries to Telegram will fail. Your bot token is removed from our servers."
        danger
        onCancel={() => setConfirmDisc(false)}
        onConfirm={() => {
          setConfirmDisc(false);
          void disconnect.mutate();
        }}
      />
      <StConfirmModal
        open={confirmDelChat != null}
        title="Remove this chat?"
        body={
          confirmDelChat
            ? `Notifications will stop being delivered to "${confirmDelChat.title}". The chat itself is unaffected.`
            : ""
        }
        danger
        onCancel={() => setConfirmDelChat(null)}
        onConfirm={() => {
          if (confirmDelChat) void delChat.mutate(confirmDelChat.id);
          setConfirmDelChat(null);
        }}
      />

      {testToast && (
        <StatusToast message={testToast.msg} variant={testToast.variant} />
      )}
    </div>
  );
}
